import type { GridApi, IDatasource, IGetRowsParams, IRowNode } from "ag-grid-community";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { RequestRow } from "./requestLogGridTypes";

import { isPostgresOnlySearch } from "@/routes/request-logs/search/older-data";
import { buildStructuredSearchPayload } from "@/routes/request-logs/search/structured-search-payload";
import { useStructuredRequestLogsRefresh } from "@/routes/request-logs/search/hooks/useStructuredRequestLogsRefresh";
import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import { useRequestLogsWorkspaceId } from "@/components/RequestsAnalytics/hooks/useRequestLogsWorkspaceId";
import type { TableColumn } from "@/components/ui/TableV2/types";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNewRequests } from "@/queries";
import {
  applyRequestLogHydrationToItems,
  buildRequestLogStubItems,
  fetchRequestsHydrate,
  getRequestLogHydrationQueryKey,
  getRequestLogHydrationQueryKeyPrefix,
  type RequestLogListItem,
} from "@/queries/requests/requestLogHydration";
import {
  fetchStructuredRequestsPaginated,
  getStructuredRequestsPaginatedQueryKey,
  getStructuredRequestsPaginatedQueryKeyPrefix,
  UseStructuredRequestsPaginatedOptions,
} from "@/queries/requests/useStructuredRequestsPaginated";
import { displayErrorToast } from "@/utils/toast";

/** Coalesce synchronous duplicate getRows in the same tick; keep near-zero for immediate refetch. */
const PAGE_LOAD_SETTLE_DELAY_MS = 0;
const PAGE_LOAD_REJECTED_CLEAR_DELAY_MS = 100;
const CURSOR_CHAIN_START_ROW = 0;
const REQUEST_LOG_HYDRATION_CACHE_MS = 5 * 60 * 1000;

const mapHydratedRequestRows = (items: RequestLogListItem[]): RequestRow[] =>
  items.map(item => ({
    ...item,
    score: item.scores?.length ? item.scores[0] : undefined,
  }));

export const getRequestLogRowId = (data: RequestRow | undefined): string => String(data?.id ?? "");

const isQueryCancellationError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.name === "AbortError" || error.name === "CancelledError";
};

const serializeGridSearchParams = (params: UseStructuredRequestsPaginatedOptions): string =>
  JSON.stringify({
    q: params.q ?? "",
    sortBy: params.sortBy ?? null,
    filterGroup: params.filterGroup,
    includeCount: params.includeCount ?? true,
  });

const serializeGridSearchParamsWithoutSort = (
  params: UseStructuredRequestsPaginatedOptions,
): string =>
  JSON.stringify({
    q: params.q ?? "",
    filterGroup: params.filterGroup,
    includeCount: params.includeCount ?? true,
  });

export const useRequestLogsGridData = (columns: TableColumn<RequestRow>[]) => {
  const structuredSearchStore = useStructuredSearchStore();
  const queryClient = useQueryClient();
  const { advanceStructuredEndIfNeeded, clearNewRequestsBadge } = useStructuredRequestLogsRefresh();
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();
  const { activeWorkspace } = useUser();
  const gridApiRef = useRef<GridApi<RequestRow> | null>(null);
  const hasCompletedInitialLoadRef = useRef(false);
  const latestGetRowsRequestIdRef = useRef(0);
  const pendingGetRowsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingGetRowsFailCallbackRef = useRef<(() => void) | null>(null);
  const rejectedGetRowsClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorByStartRowRef = useRef<Map<number, Record<string, unknown> | null>>(
    new Map([[CURSOR_CHAIN_START_ROW, null]]),
  );
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const [isPageLoadPending, setIsPageLoadPending] = useState(false);
  const [lastRowCount, setLastRowCount] = useState<number | null>(null);
  const structuredRequestsFetchingCount = useIsFetching({
    queryKey: getStructuredRequestsPaginatedQueryKeyPrefix(userToken, workspaceId),
    exact: false,
  });

  const normalizedSortId = useMemo(() => {
    const currentSortId = structuredSearchStore.sorting[0]?.id;
    if (!currentSortId) {
      return undefined;
    }

    if (!currentSortId.startsWith("col_")) {
      return currentSortId;
    }

    const matchedColumn = columns.find(column => {
      const columnId =
        "id" in column && typeof column.id === "number" ? `col_${column.id}` : undefined;
      return columnId === currentSortId;
    });

    return typeof matchedColumn?.field === "string" ? matchedColumn.field : undefined;
  }, [columns, structuredSearchStore.sorting]);

  const sortBy = useMemo(() => {
    if (!normalizedSortId) {
      return undefined;
    }

    return {
      id: normalizedSortId,
      desc: structuredSearchStore.sorting[0].desc,
    };
  }, [normalizedSortId, structuredSearchStore.sorting]);

  const structuredSearchPayload = useMemo(
    () =>
      buildStructuredSearchPayload(structuredSearchStore.filters, structuredSearchStore.dateRange),
    [structuredSearchStore.filters, structuredSearchStore.dateRange],
  );

  const includeCount = useMemo(
    () =>
      !isPostgresOnlySearch({
        dateRange: structuredSearchStore.dateRange,
        cutoverDate: activeWorkspace?.search_cutover_date,
      }),
    [activeWorkspace?.search_cutover_date, structuredSearchStore.dateRange],
  );

  const searchParams = useMemo<UseStructuredRequestsPaginatedOptions>(
    () => ({
      ...(structuredSearchStore.q ? { q: structuredSearchStore.q } : {}),
      ...(sortBy ? { sortBy } : {}),
      filterGroup: structuredSearchPayload.filterGroup,
      includeCount,
    }),
    [includeCount, sortBy, structuredSearchPayload, structuredSearchStore.q],
  );

  // Keep a ref to the latest searchParams so that the datasource closure
  // always reads current values without needing to recreate the datasource
  // object (which triggers AG Grid to refetch).  The datasource is only
  // recreated when truly structural deps change (token, workspace, pageSize).
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const searchParamsKey = useMemo(() => serializeGridSearchParams(searchParams), [searchParams]);
  const searchParamsWithoutSortKey = useMemo(
    () => serializeGridSearchParamsWithoutSort(searchParams),
    [searchParams],
  );
  const searchParamsWithoutSortKeyRef = useRef(searchParamsWithoutSortKey);
  const lastRefreshedSearchParamsKeyRef = useRef<string | null>(null);
  const { urlHydrationReady } = structuredSearchStore;

  const clearPendingGetRows = useCallback(() => {
    if (pendingGetRowsTimeoutRef.current) {
      clearTimeout(pendingGetRowsTimeoutRef.current);
      pendingGetRowsTimeoutRef.current = null;
    }
    if (pendingGetRowsFailCallbackRef.current) {
      pendingGetRowsFailCallbackRef.current();
      pendingGetRowsFailCallbackRef.current = null;
    }
  }, []);

  const scheduleRejectedGetRowsClear = useCallback((requestId: number) => {
    if (rejectedGetRowsClearTimeoutRef.current) {
      clearTimeout(rejectedGetRowsClearTimeoutRef.current);
    }

    rejectedGetRowsClearTimeoutRef.current = setTimeout(() => {
      rejectedGetRowsClearTimeoutRef.current = null;
      if (requestId === latestGetRowsRequestIdRef.current) {
        setIsPageLoadPending(false);
      }
    }, PAGE_LOAD_REJECTED_CLEAR_DELAY_MS);
  }, []);

  const datasource = useMemo<IDatasource | undefined>(() => {
    if (!userToken || !workspaceId || !urlHydrationReady) {
      return undefined;
    }

    return {
      rowCount: undefined,
      getRows: (params: IGetRowsParams) => {
        const requestedPageSize = Math.max(
          structuredSearchStore.pageSize,
          params.endRow - params.startRow || structuredSearchStore.pageSize,
        );
        const page = Math.floor(params.startRow / requestedPageSize) + 1;
        const latestSearchParams = searchParamsRef.current;
        const fetchPage = (pageNumber: number, afterKey: Record<string, unknown> | null) =>
          queryClient.fetchQuery(
            getStructuredRequestsPaginatedQueryKey({
              userToken,
              workspaceId,
              page: pageNumber,
              pageSize: requestedPageSize,
              searchParams: latestSearchParams,
              afterKey,
            }),
            ({ signal }) =>
              fetchStructuredRequestsPaginated({
                signal,
                userToken,
                page: pageNumber,
                pageSize: requestedPageSize,
                searchParams: latestSearchParams,
                workspaceId,
                afterKey,
              }),
            {
              staleTime: 0,
              cacheTime: 5 * 60 * 1000,
            },
          );
        const hydrateRowsForPage = async (
          stubs: RequestLogListItem[],
          sourceFieldsByRequestId?: Record<string, Record<string, unknown>>,
          requestId?: number,
        ) => {
          const requestLogIds = stubs
            .map(item => Number(item.id))
            .filter(id => Number.isFinite(id));
          if (requestLogIds.length === 0) {
            return;
          }

          try {
            const hydration = await queryClient.fetchQuery(
              getRequestLogHydrationQueryKey({
                workspaceId,
                requestLogIds,
                sourceFieldsByRequestId,
              }),
              ({ signal }) =>
                fetchRequestsHydrate({
                  signal,
                  userToken,
                  workspaceId,
                  requestLogIds,
                  sourceFieldsByRequestId,
                }),
              {
                staleTime: REQUEST_LOG_HYDRATION_CACHE_MS,
                cacheTime: REQUEST_LOG_HYDRATION_CACHE_MS,
              },
            );
            if (requestId !== undefined && requestId !== latestGetRowsRequestIdRef.current) {
              return;
            }
            const hydratedItems = applyRequestLogHydrationToItems({
              stubs,
              hydratedItems: hydration.items,
              status: "complete",
            });
            const api = gridApiRef.current;
            if (!api) {
              return;
            }

            const rowNodes: IRowNode<RequestRow>[] = [];
            mapHydratedRequestRows(hydratedItems).forEach(item => {
              const rowNode = api.getRowNode(String(item.id));
              if (!rowNode?.data) {
                return;
              }
              rowNode.setData(item);
              rowNodes.push(rowNode);
            });

            if (rowNodes.length > 0) {
              api.refreshCells({ rowNodes, force: true });
            }
          } catch (error) {
            if (isQueryCancellationError(error)) {
              return;
            }
            const api = gridApiRef.current;
            if (!api) {
              return;
            }
            if (requestId !== undefined && requestId !== latestGetRowsRequestIdRef.current) {
              return;
            }

            const fallbackItems = applyRequestLogHydrationToItems({
              stubs,
              status: "error",
            });
            const rowNodes: IRowNode<RequestRow>[] = [];
            mapHydratedRequestRows(fallbackItems).forEach(item => {
              const rowNode = api.getRowNode(String(item.id));
              if (!rowNode?.data) {
                return;
              }
              rowNode.setData(item);
              rowNodes.push(rowNode);
            });

            if (rowNodes.length > 0) {
              api.refreshCells({ rowNodes, force: true });
            }
          }
        };
        const resolveAfterKeyForStartRow = async (): Promise<{
          afterKey: Record<string, unknown> | null;
          lastRow?: number;
          usePagePagination?: boolean;
        }> => {
          if (params.startRow === CURSOR_CHAIN_START_ROW) {
            return { afterKey: null };
          }

          const cachedAfterKey = cursorByStartRowRef.current.get(params.startRow);
          if (cachedAfterKey !== undefined) {
            return { afterKey: cachedAfterKey };
          }

          let cursor = cursorByStartRowRef.current.get(CURSOR_CHAIN_START_ROW) ?? null;
          for (
            let startRow = CURSOR_CHAIN_START_ROW;
            startRow < params.startRow;
            startRow += requestedPageSize
          ) {
            const nextStartRow = startRow + requestedPageSize;
            const knownNextCursor = cursorByStartRowRef.current.get(nextStartRow);
            if (knownNextCursor !== undefined) {
              cursor = knownNextCursor;
              continue;
            }

            const cursorPage = Math.floor(startRow / requestedPageSize) + 1;
            const response = await fetchPage(cursorPage, cursor);
            if (response.pagination_mode !== "cursor") {
              return { afterKey: null, usePagePagination: true };
            }

            const pageItemCount = response.request_log_ids?.length ?? response.items.length;
            if (!response.next_after_key) {
              const lastRow = startRow + pageItemCount;
              cursorByStartRowRef.current.set(nextStartRow, null);
              return { afterKey: null, lastRow };
            }

            cursorByStartRowRef.current.set(nextStartRow, response.next_after_key);
            cursor = response.next_after_key;
          }

          return { afterKey: cursorByStartRowRef.current.get(params.startRow) ?? null };
        };
        const requestId = latestGetRowsRequestIdRef.current + 1;
        latestGetRowsRequestIdRef.current = requestId;

        clearPendingGetRows();
        setIsPageLoadPending(true);
        pendingGetRowsFailCallbackRef.current = params.failCallback;

        pendingGetRowsTimeoutRef.current = setTimeout(async () => {
          pendingGetRowsTimeoutRef.current = null;
          pendingGetRowsFailCallbackRef.current = null;

          const currentPage = gridApiRef.current
            ? gridApiRef.current.paginationGetCurrentPage() + 1
            : null;

          if (requestId !== latestGetRowsRequestIdRef.current) {
            params.failCallback();
            return;
          }

          if (currentPage !== null && currentPage !== page) {
            params.failCallback();
            scheduleRejectedGetRowsClear(requestId);
            return;
          }

          try {
            const cursorResolution = await resolveAfterKeyForStartRow();
            if (requestId !== latestGetRowsRequestIdRef.current) {
              params.failCallback();
              return;
            }
            if (cursorResolution.lastRow !== undefined) {
              params.successCallback([], cursorResolution.lastRow);
              setLastRowCount(cursorResolution.lastRow);
              if (!hasCompletedInitialLoadRef.current) {
                hasCompletedInitialLoadRef.current = true;
                setHasCompletedInitialLoad(true);
              }
              return;
            }

            const response = await fetchPage(
              page,
              cursorResolution.usePagePagination ? null : cursorResolution.afterKey,
            );
            if (requestId !== latestGetRowsRequestIdRef.current) {
              params.failCallback();
              return;
            }

            const stubs = buildRequestLogStubItems(response);
            let lastRow: number | undefined;
            if (response.pagination_mode === "cursor") {
              if (response.next_after_key) {
                cursorByStartRowRef.current.set(params.endRow, response.next_after_key);
                lastRow = undefined;
              } else {
                lastRow = params.startRow + stubs.length;
              }
              if (params.startRow === 0 && stubs.length === 0) {
                setLastRowCount(0);
              } else {
                setLastRowCount(null);
              }
            } else {
              setLastRowCount(
                response.total ?? (stubs.length === 0 && !response.has_next ? 0 : null),
              );
              lastRow =
                response.total ??
                (stubs.length === 0 && !response.has_next ? params.startRow : undefined);
            }
            params.successCallback(mapHydratedRequestRows(stubs), lastRow);
            void hydrateRowsForPage(stubs, response.source_fields_by_request_id, requestId);
            if (!hasCompletedInitialLoadRef.current) {
              hasCompletedInitialLoadRef.current = true;
              setHasCompletedInitialLoad(true);
            }
          } catch (error) {
            if (isQueryCancellationError(error)) {
              params.failCallback();
              return;
            }
            if (requestId !== latestGetRowsRequestIdRef.current) {
              params.failCallback();
              return;
            }
            setLastRowCount(null);
            params.failCallback();
            displayErrorToast("Error loading requests");
          } finally {
            if (requestId === latestGetRowsRequestIdRef.current) {
              setIsPageLoadPending(false);
            }
          }
        }, PAGE_LOAD_SETTLE_DELAY_MS);
      },
    };
  }, [
    clearPendingGetRows,
    queryClient,
    scheduleRejectedGetRowsClear,
    structuredSearchStore.pageSize,
    urlHydrationReady,
    userToken,
    workspaceId,
  ]);

  useEffect(
    () => () => {
      clearPendingGetRows();
      if (rejectedGetRowsClearTimeoutRef.current) {
        clearTimeout(rejectedGetRowsClearTimeoutRef.current);
      }
    },
    [clearPendingGetRows],
  );

  const refreshGridForCurrentSearchParams = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) {
      return false;
    }

    const currentSearchParamsKey = serializeGridSearchParams(searchParamsRef.current);
    if (lastRefreshedSearchParamsKeyRef.current === currentSearchParamsKey) {
      return true;
    }

    latestGetRowsRequestIdRef.current += 1;
    void queryClient.cancelQueries({
      queryKey: getStructuredRequestsPaginatedQueryKeyPrefix(userToken, workspaceId),
      exact: false,
    });
    void queryClient.cancelQueries({
      queryKey: getRequestLogHydrationQueryKeyPrefix(workspaceId),
      exact: false,
    });
    clearPendingGetRows();
    setIsPageLoadPending(false);
    cursorByStartRowRef.current = new Map([[CURSOR_CHAIN_START_ROW, null]]);
    api.refreshInfiniteCache();
    lastRefreshedSearchParamsKeyRef.current = currentSearchParamsKey;
    return true;
  }, [clearPendingGetRows, queryClient, userToken, workspaceId]);

  // When searchParams change (filters applied/removed or URL hydration completes),
  // refresh the grid's infinite cache. Because the datasource reads from
  // searchParamsRef, this ensures the next fetch uses the latest params without
  // recreating the datasource object (which would cause the double-request race).
  const isInitialSearchParams = useRef(true);
  useEffect(() => {
    if (isInitialSearchParams.current) {
      isInitialSearchParams.current = false;
      searchParamsWithoutSortKeyRef.current = searchParamsWithoutSortKey;
      return;
    }

    const isSortOnlyChange = searchParamsWithoutSortKeyRef.current === searchParamsWithoutSortKey;
    searchParamsWithoutSortKeyRef.current = searchParamsWithoutSortKey;
    if (isSortOnlyChange) {
      lastRefreshedSearchParamsKeyRef.current = serializeGridSearchParams(searchParamsRef.current);
      return;
    }

    refreshGridForCurrentSearchParams();
  }, [refreshGridForCurrentSearchParams, searchParamsKey, searchParamsWithoutSortKey]);

  useEffect(() => {
    if (!urlHydrationReady) {
      return;
    }
    refreshGridForCurrentSearchParams();
  }, [refreshGridForCurrentSearchParams, urlHydrationReady]);

  const setGridApiRef = useCallback(
    (api: GridApi<RequestRow>) => {
      gridApiRef.current = api;
      refreshGridForCurrentSearchParams();
    },
    [refreshGridForCurrentSearchParams],
  );

  const isTableLoading = isPageLoadPending || structuredRequestsFetchingCount > 0;

  useEffect(() => {
    if (!gridApiRef.current || isTableLoading) {
      return;
    }

    if (lastRowCount === 0) {
      gridApiRef.current.showNoRowsOverlay();
      return;
    }

    gridApiRef.current.hideOverlay();
  }, [isTableLoading, lastRowCount]);

  const handleManualRefresh = useCallback(() => {
    try {
      clearNewRequestsBadge();
      setLastRowCount(null);
      clearPendingGetRows();
      cursorByStartRowRef.current = new Map([[CURSOR_CHAIN_START_ROW, null]]);
      lastRefreshedSearchParamsKeyRef.current = null;

      const shouldAdvanceEnd = advanceStructuredEndIfNeeded();

      latestGetRowsRequestIdRef.current += 1;
      void queryClient.cancelQueries({
        queryKey: getStructuredRequestsPaginatedQueryKeyPrefix(userToken, workspaceId),
        exact: false,
      });
      void queryClient.cancelQueries({
        queryKey: getRequestLogHydrationQueryKeyPrefix(workspaceId),
        exact: false,
      });
      void queryClient.removeQueries({
        queryKey: getStructuredRequestsPaginatedQueryKeyPrefix(userToken, workspaceId),
        exact: false,
      });
      void queryClient.removeQueries({
        queryKey: getRequestLogHydrationQueryKeyPrefix(workspaceId),
        exact: false,
      });

      structuredSearchStore.resetToFirstPage();

      queueMicrotask(() => {
        const api = gridApiRef.current;
        if (!api) {
          return;
        }
        api.paginationGoToFirstPage();
        // When the date range changes, `searchParams` updates and the effect below refreshes the grid.
        if (!shouldAdvanceEnd) {
          api.refreshInfiniteCache();
        }
      });
    } catch {
      displayErrorToast("Error refreshing requests");
    }
  }, [
    advanceStructuredEndIfNeeded,
    clearNewRequestsBadge,
    clearPendingGetRows,
    queryClient,
    structuredSearchStore,
    userToken,
    workspaceId,
  ]);

  const { data: newRequestsData } = useNewRequests();
  const newRequestsCount = newRequestsData?.count_requests ?? 0;
  const hasNewData = newRequestsCount > 0;

  return {
    datasource,
    setGridApiRef,
    hasCompletedInitialLoad,
    handleManualRefresh,
    hasNewData,
    isTableLoading,
    totalRows: lastRowCount,
  };
};
